import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { AcademicState, SetAcademicWebsite, StartAcademicVerification, UserType } from '@platform-frontend/checkout';
import { LicenseScope } from '@platform-frontend/marketplace';
import { CustomValidators, InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

interface AcademicWebsiteForm {
  website: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'yat-verify-employee',
  templateUrl: './verify-employee.component.html',
  styleUrls: ['./verify-employee.component.scss']
})
export class VerifyEmployeeComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private store: Store) {}

  readonly academicWebsiteForm = new FormGroup<AcademicWebsiteForm>({
    website: new FormControl(null, { nonNullable: true, validators: CustomValidators.looseUrl })
  });

  @Select(AcademicState.licenseScope)
  licenseScope$: Observable<LicenseScope | null>;

  @Select(AcademicState.academicWebsite)
  academicWebsite$: Observable<string | null>;

  @Select(AcademicState.userType)
  userType$: Observable<UserType>;

  verifyOnContinue$: Observable<boolean>;

  startAcademicVerificationActionFilter = new InclusiveActionFilter(StartAcademicVerification);

  ngOnInit(): void {
    this.verifyOnContinue$ = this.licenseScope$.pipe(map(licenseScope => licenseScope === 'PERSONAL_USE'));

    this.academicWebsite$
      .pipe(
        tap(academicWebsite => this.academicWebsiteForm.patchValue({ website: academicWebsite })),
        untilDestroyed(this)
      )
      .subscribe();
  }

  submitForm(value: { website: string }): void {
    this.store
      .dispatch(new SetAcademicWebsite(value.website))
      .pipe(
        switchMap(() => this.verifyOnContinue$),
        take(1),
        switchMap(verifyOnContinue =>
          verifyOnContinue ? this.startAcademicVerification() : this.store.dispatch(new Navigate(['../ip-range'], { relativeTo: this.activatedRoute }))
        ),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private startAcademicVerification(): Observable<void> {
    return this.store
      .dispatch(new StartAcademicVerification())
      .pipe(switchMap(() => this.store.dispatch(new Navigate(['../verify-academic-email'], { relativeTo: this.activatedRoute }))));
  }

  goBack(): void {
    this.store
      .dispatch(new SetAcademicWebsite(this.academicWebsiteForm.value.website ?? ''))
      .pipe(
        switchMap(() => this.userType$),
        switchMap(userType =>
          userType === 'TEACHER'
            ? this.store.dispatch(new Navigate(['../teacher-toggle'], { relativeTo: this.activatedRoute }))
            : this.store.dispatch(new Navigate(['../academic-registration'], { relativeTo: this.activatedRoute }))
        ),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
